import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShoppingCart, Download, Lock, Volume2, VolumeX, Heart, Star as StarIcon, Sparkles, X, Rainbow as RainbowIcon, Smile, Share2, Copy, Check as CheckIcon, Eye, Languages, Mic, Square, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import client, { mediaUrl } from "@/lib/api";
import { payForBook } from "@/lib/pay";
import UpiPay from "@/components/UpiPay";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { TerrySleeping } from "@/components/Terry";
import ReaderPage from "@/components/ReaderPage";
import { shipping as validateShipping } from "@/lib/validate";

const STICKERS = [
    { type: "heart", icon: Heart, color: "#FF6B9D", fill: true },
    { type: "star", icon: StarIcon, color: "#F59E0B", fill: true },
    { type: "sparkle", icon: Sparkles, color: "#C4B5FD", fill: false },
    { type: "rainbow", icon: RainbowIcon, color: "#7DD3FC", fill: false },
    { type: "smile", icon: Smile, color: "#86EFAC", fill: false },
];

const LANGS = [
    { code: "en", label: "English", tts: "en-US" },
    { code: "hi", label: "हिन्दी", tts: "hi-IN" },
    { code: "ta", label: "தமிழ்", tts: "ta-IN" },
    { code: "mr", label: "मराठी", tts: "mr-IN" },
];

export default function BookReader() {
    const { user } = useAuth();
    const { id } = useParams();
    const nav = useNavigate();
    const [book, setBook] = useState(null);
    const [idx, setIdx] = useState(0);
    const [buyOpen, setBuyOpen] = useState(false);
    const [method, setMethod] = useState("razorpay");
    const [upiOrder, setUpiOrder] = useState(null);
    const [addrOpen, setAddrOpen] = useState(false);
    const [addr, setAddr] = useState({ name: "", line1: "", city: "", pin: "", phone: "" });
    const addrError = validateShipping(addr);

    // Narrator
    const [narrating, setNarrating] = useState(false);
    const utterRef = useRef(null);

    // Family voice. A synthesised voice is a commodity every competitor has; the
    // one where Nani mispronounces "dinosaur" is the reason this book gets kept.
    const [voices, setVoices] = useState({});      // { "4": {url, by, at} }
    const [recording, setRecording] = useState(null);  // page_number, or null
    const [uploading, setUploading] = useState(null);
    const recRef = useRef(null);
    const audioRef = useRef(null);

    // Stickers
    const [selectedSticker, setSelectedSticker] = useState(null);
    const leftBoxRef = useRef(null);
    const rightBoxRef = useRef(null);
    // The leaf being turned, so the animation can finish before the spread swaps.
    const [turning, setTurning] = useState(null); // null | "fwd" | "back"
    const [pdfBusy, setPdfBusy] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareLink, setShareLink] = useState("");
    const [shareBusy, setShareBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    // Multi-language
    const [lang, setLang] = useState("en");
    const [translations, setTranslations] = useState({}); // { hi: {title, pages:[]}, ... }
    const [translating, setTranslating] = useState(false);

    useEffect(() => {
        client.get(`/books/${id}`).then(({ data }) => {
            setBook(data);
            setVoices(data.voices || {});
        }).catch(() => toast.error("Book not found"));
    }, [id]);

    // Content protection
    useEffect(() => {
        const noContext = (e) => { e.preventDefault(); };
        const noShortcut = (e) => {
            if (
                e.key === "PrintScreen" ||
                (e.ctrlKey && (e.key === "p" || e.key === "s")) ||
                (e.metaKey && (e.key === "p" || e.key === "s")) ||
                e.key === "F12" ||
                ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j"))
            ) {
                e.preventDefault();
                toast.warning("This booklet is protected from copying.");
            }
        };
        const onVis = () => {
            if (document.hidden) document.body.classList.add("window-hidden");
            else document.body.classList.remove("window-hidden");
        };
        document.addEventListener("contextmenu", noContext);
        document.addEventListener("keydown", noShortcut);
        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("blur", onVis);
        window.addEventListener("focus", onVis);
        return () => {
            document.removeEventListener("contextmenu", noContext);
            document.removeEventListener("keydown", noShortcut);
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("blur", onVis);
            window.removeEventListener("focus", onVis);
            document.body.classList.remove("window-hidden");
            // Stop narrator on unmount — both the synthesised voice and a family
            // recording, or leaving the page keeps Nani talking in a dead tab.
            try { window.speechSynthesis.cancel(); } catch {}
            if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
            try { recRef.current?.stop(); } catch {}
        };
    }, []);

    if (!book) return <div className="min-h-screen reader-night grid place-items-center font-display text-2xl text-paper">Loading your book…</div>;

    const pages = book.pages || [];
    const purchased = book.purchased;
    // Apply active language translation for display text (image_url stays as-is)
    const activeTx = lang !== "en" && translations[lang];
    const displayTitle = activeTx?.title || book.title;
    // ponytail: plain expression, not useMemo — a hook here sits after the `if (!book)`
    // early return, which breaks rules-of-hooks. Mapping ~16 pages per render is free.
    const displayPages = activeTx
        ? pages.map((p) => ({ ...p, text: new Map(activeTx.pages.map((t) => [t.page_number, t.text])).get(p.page_number) || p.text }))
        : pages;
    // `idx` is still the focused page — the narrator walks one page at a time —
    // and the open spread is simply the leaf-pair that page falls in.
    const spreadStart = idx - (idx % 2);
    const leftPage = displayPages[spreadStart];
    const rightPage = displayPages[spreadStart + 1];
    const lockedFrom = 3; // first three pages are the free preview
    const reactionsFor = (p) => (book.reactions && book.reactions[String(p?.page_number)]) || [];
    const stickerConfig = (type) => STICKERS.find((x) => x.type === type) || STICKERS[0];

    const switchLang = async (code) => {
        if (code === lang) return;
        if (code === "en") { setLang("en"); stopNarrator(); return; }
        if (translations[code]) { setLang(code); stopNarrator(); return; }
        setTranslating(true);
        stopNarrator();
        try {
            const { data } = await client.post(`/books/${book.id}/translate`, { language: code });
            setTranslations((prev) => ({ ...prev, [code]: { title: data.title, pages: data.pages } }));
            setLang(code);
            toast.success(`Story translated ✨`);
        } catch (e) {
            toast.error("Could not translate right now");
        } finally { setTranslating(false); }
    };

    const stopNarrator = () => {
        try { window.speechSynthesis.cancel(); } catch {}
        if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
        setNarrating(false);
    };

    // Both narrators end a page the same way, so the walk lives in one place.
    const advanceFrom = (cur) => {
        const nextIdx = cur + 1;
        if (nextIdx >= displayPages.length || (!purchased && nextIdx > 2)) {
            setNarrating(false);
            return cur;
        }
        setTimeout(() => speakPage(nextIdx), 400);
        return nextIdx;
    };

    const speakPage = (pageIdx) => {
        const p = displayPages[pageIdx];
        if (!p) return;

        // A real family recording always wins over the synthesised voice — including
        // in a translated book, where the point is whose voice it is, not the words.
        const recorded = voices[String(p.page_number)];
        if (recorded?.url) {
            try { window.speechSynthesis.cancel(); } catch {}
            if (audioRef.current) audioRef.current.pause();
            const audio = new Audio(mediaUrl(recorded.url));
            audio.onended = () => setIdx(advanceFrom);
            audio.onerror = () => setNarrating(false);
            audioRef.current = audio;
            audio.play().catch(() => setNarrating(false));
            return;
        }

        if (typeof window === "undefined" || !("speechSynthesis" in window)) {
            toast.error("Your browser doesn't support the narrator voice.");
            return;
        }
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(p.text);
        const langCfg = LANGS.find((l) => l.code === lang) || LANGS[0];
        const voices = window.speechSynthesis.getVoices();
        // Prefer a voice matching the selected language
        const preferred = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(langCfg.tts.toLowerCase().slice(0, 2)))
            || voices.find((v) => /female|samantha|karen|serena|zira|joanna|aria/i.test(v.name))
            || voices[0];
        if (preferred) utter.voice = preferred;
        utter.lang = langCfg.tts;
        utter.rate = 0.92;
        utter.pitch = 1.05;
        utter.onend = () => setIdx(advanceFrom);
        utter.onerror = () => setNarrating(false);
        utterRef.current = utter;
        window.speechSynthesis.speak(utter);
    };

    const toggleNarrator = () => {
        if (narrating) { stopNarrator(); return; }
        setNarrating(true);
        speakPage(idx);
    };

    // ---- Family voice
    const startRecording = async (pageNumber) => {
        if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
            toast.error("This browser can't record audio. Try Chrome or Safari.");
            return;
        }
        stopNarrator();
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const rec = new MediaRecorder(stream);
            const chunks = [];
            rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
            rec.onstop = async () => {
                // Release the mic before the upload, not after — a failed upload
                // must not leave the recording light on.
                stream.getTracks().forEach((t) => t.stop());
                setUploading(pageNumber);
                try {
                    const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
                    const form = new FormData();
                    form.append("file", blob, "voice.webm");
                    form.append("by", user?.name || "");
                    const { data } = await client.post(`/books/${book.id}/voice/${pageNumber}`, form);
                    setVoices((v) => ({ ...v, [String(pageNumber)]: data.voice }));
                    toast.success("Saved in your voice ♥");
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Could not save that recording.");
                } finally { setUploading(null); }
            };
            rec.start();
            recRef.current = rec;
            setRecording(pageNumber);
        } catch (e) {
            toast.error("We couldn't reach your microphone.");
        }
    };

    const stopRecording = () => {
        try { recRef.current?.stop(); } catch {}
        recRef.current = null;
        setRecording(null);
    };

    const clearVoice = async (pageNumber) => {
        setVoices((v) => { const next = { ...v }; delete next[String(pageNumber)]; return next; });
        await client.delete(`/books/${book.id}/voice/${pageNumber}`)
            .catch(() => toast.error("Could not remove that recording."));
    };

    // A turn moves a whole leaf-pair, and waits for the paper to land.
    const TURN_MS = 620;
    const next = () => {
        if (turning || spreadStart + 2 >= pages.length) return;
        stopNarrator(); setTurning("fwd");
        setTimeout(() => { setIdx(spreadStart + 2); setTurning(null); }, TURN_MS);
    };
    const prev = () => {
        if (turning || spreadStart === 0) return;
        stopNarrator(); setTurning("back");
        setTimeout(() => { setIdx(spreadStart - 2); setTurning(null); }, TURN_MS);
    };

    // Sticker placement. x/y are fractions of the clicked leaf, not of the
    // spread, so they mean the same thing they did before the spread landed.
    const placeSticker = (target, boxRef) => async (e) => {
        if (!selectedSticker || !target) return;
        const rect = boxRef.current?.getBoundingClientRect();
        if (!rect) return;
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        try {
            const { data } = await client.post(`/books/${book.id}/reactions`, {
                page_number: target.page_number, type: selectedSticker, x, y,
            });
            const key = String(target.page_number);
            const cur = book.reactions?.[key] || [];
            setBook({ ...book, reactions: { ...(book.reactions || {}), [key]: [...cur, data.reaction] } });
        } catch { toast.error("Couldn't place the sticker"); }
    };

    const removeSticker = (pageNumber) => async (rid) => {
        try {
            await client.delete(`/books/${book.id}/reactions/${pageNumber}/${rid}`);
            const key = String(pageNumber);
            const cur = book.reactions?.[key] || [];
            setBook({ ...book, reactions: { ...(book.reactions || {}), [key]: cur.filter((r) => r.id !== rid) } });
        } catch {}
    };

    // Print PDF Kit (client-side) — story pages + bookplate stickers + read-to-me ribbon
    const buildPDF = async () => {
        if (!purchased) { toast.error("Purchase to download the printable PDF."); return; }
        setPdfBusy(true);
        try {
            const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
            const pageW = 210, pageH = 297;
            const trimW = 148, trimH = 210;
            const marginX = (pageW - trimW) / 2;
            const marginY = (pageH - trimH) / 2;
            const childName = (book.title || "").split(" ")[0] || "your child";

            const drawCropMarks = () => {
                doc.setDrawColor(160);
                doc.setLineWidth(0.15);
                const g = 6;
                [
                    [marginX, marginY], [marginX + trimW, marginY],
                    [marginX, marginY + trimH], [marginX + trimW, marginY + trimH],
                ].forEach(([x, y]) => {
                    doc.line(x - g, y, x - 1, y);
                    doc.line(x + 1, y, x + g, y);
                    doc.line(x, y - g, x, y - 1);
                    doc.line(x, y + 1, x, y + g);
                });
            };

            // ---- Story pages ----
            for (let i = 0; i < pages.length; i++) {
                const p = pages[i];
                if (i > 0) doc.addPage();
                drawCropMarks();

                if (p.image_url) {
                    const img = await loadImage(mediaUrl(p.image_url));
                    const imgH = trimH * 0.62;
                    const imgW = trimW - 20;
                    const ix = marginX + (trimW - imgW) / 2;
                    const iy = marginY + 15;
                    try { doc.addImage(img, "PNG", ix, iy, imgW, imgH, undefined, "FAST"); } catch {}
                }

                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(`${p.page_number} / ${pages.length}`, marginX + trimW / 2, marginY + trimH - 10, { align: "center" });

                doc.setFont("helvetica", "normal");
                doc.setFontSize(13);
                doc.setTextColor(45, 27, 78);
                const textY = marginY + trimH * 0.70;
                const lines = doc.splitTextToSize(p.text || "", trimW - 30);
                doc.text(lines, marginX + trimW / 2, textY, { align: "center", lineHeightFactor: 1.4 });

                doc.setFontSize(7);
                doc.setTextColor(255, 107, 157);
                doc.text("Tales with Terry", marginX + trimW / 2, marginY + trimH - 3, { align: "center" });
            }

            // ---- Bookplate stickers page (4 per A4) ----
            doc.addPage();
            drawCropMarks();
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.setTextColor(45, 27, 78);
            doc.text("Bookplate Stickers", pageW / 2, 20, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text("Cut along the dotted lines and stick inside the book cover.", pageW / 2, 27, { align: "center" });

            const cols = 2, rows = 2;
            const gap = 6;
            const kitW = pageW - marginX * 2;
            const cellW = (kitW - gap * (cols - 1)) / cols;
            const cellH = (trimH - 45 - gap * (rows - 1)) / rows;
            const startY = 40;
            const palettes = [
                { bg: [255, 251, 245], accent: [255, 107, 157] },
                { bg: [253, 230, 138], accent: [45, 27, 78] },
                { bg: [196, 181, 253], accent: [45, 27, 78] },
                { bg: [134, 239, 172], accent: [45, 27, 78] },
            ];
            for (let r = 0; r < rows; r++) {
                for (let c = 0; c < cols; c++) {
                    const x = marginX + c * (cellW + gap);
                    const y = startY + r * (cellH + gap);
                    const pal = palettes[r * cols + c];
                    // Rounded rect background
                    doc.setFillColor(...pal.bg);
                    doc.roundedRect(x, y, cellW, cellH, 6, 6, "F");
                    // Dotted cut border
                    doc.setDrawColor(160);
                    doc.setLineDashPattern([1.5, 1.5], 0);
                    doc.setLineWidth(0.3);
                    doc.roundedRect(x, y, cellW, cellH, 6, 6, "S");
                    doc.setLineDashPattern([], 0);
                    // Text
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(11);
                    doc.setTextColor(...pal.accent);
                    doc.text("This book belongs to", x + cellW / 2, y + cellH * 0.32, { align: "center" });
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(20);
                    const nameLines = doc.splitTextToSize(childName, cellW - 12);
                    doc.text(nameLines, x + cellW / 2, y + cellH * 0.55, { align: "center" });
                    doc.setFont("helvetica", "italic");
                    doc.setFontSize(8);
                    doc.setTextColor(120);
                    doc.text("A Tales with Terry keepsake", x + cellW / 2, y + cellH - 8, { align: "center" });
                }
            }

            // ---- Read-to-Me ribbon page ----
            doc.addPage();
            drawCropMarks();
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.setTextColor(45, 27, 78);
            doc.text("Read-to-Me Ribbon", pageW / 2, 20, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text("Cut out both ribbons. Slip one into any page you'd like read at bedtime.", pageW / 2, 27, { align: "center" });

            const ribbonW = 30, ribbonH = 140;
            const ribbonY = 45;
            const ribbonXs = [marginX + 20, marginX + 20 + ribbonW + 30, marginX + 20 + (ribbonW + 30) * 2];
            const ribbonColors = [
                [255, 107, 157],
                [125, 211, 252],
                [134, 239, 172],
            ];
            ribbonXs.forEach((rx, i) => {
                doc.setFillColor(...ribbonColors[i]);
                // Ribbon body
                doc.rect(rx, ribbonY, ribbonW, ribbonH - 12, "F");
                // V-shaped bottom (two triangles)
                doc.triangle(rx, ribbonY + ribbonH - 12, rx + ribbonW / 2, ribbonY + ribbonH, rx + ribbonW / 2, ribbonY + ribbonH - 12, "F");
                doc.triangle(rx + ribbonW / 2, ribbonY + ribbonH - 12, rx + ribbonW, ribbonY + ribbonH - 12, rx + ribbonW / 2, ribbonY + ribbonH, "F");
                // Dotted cut border around whole shape (approx via rect + tri outlines)
                doc.setDrawColor(90);
                doc.setLineDashPattern([1.2, 1.2], 0);
                doc.setLineWidth(0.25);
                doc.rect(rx, ribbonY, ribbonW, ribbonH - 12);
                doc.line(rx, ribbonY + ribbonH - 12, rx + ribbonW / 2, ribbonY + ribbonH);
                doc.line(rx + ribbonW, ribbonY + ribbonH - 12, rx + ribbonW / 2, ribbonY + ribbonH);
                doc.setLineDashPattern([], 0);
                // Text
                doc.setTextColor(255, 255, 255);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.text("READ", rx + ribbonW / 2, ribbonY + 30, { align: "center" });
                doc.text("TO", rx + ribbonW / 2, ribbonY + 45, { align: "center" });
                doc.text("ME", rx + ribbonW / 2, ribbonY + 60, { align: "center" });
                doc.setFont("helvetica", "italic");
                doc.setFontSize(9);
                doc.text("tonight", rx + ribbonW / 2, ribbonY + 78, { align: "center" });
                // Hole punch dot
                doc.setDrawColor(255, 255, 255);
                doc.setFillColor(255, 255, 255);
                doc.circle(rx + ribbonW / 2, ribbonY + 10, 2, "F");
            });
            doc.setFont("helvetica", "italic");
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text("Tip: punch a small hole through the dot and tie a ribbon so it hangs from the page.", pageW / 2, ribbonY + ribbonH + 15, { align: "center" });

            doc.setFontSize(7);
            doc.setTextColor(255, 107, 157);
            doc.text("Tales with Terry · Print Kit", pageW / 2, pageH - 8, { align: "center" });

            doc.save(`${(book.title || "Tales with Terry").replace(/[^\w]+/g, "_")}_PrintKit.pdf`);
            toast.success("Your Print Kit is ready! Story pages, bookplate stickers & read-to-me ribbons inside.");
        } catch (e) {
            console.error(e);
            toast.error("Couldn't build the PDF");
        } finally { setPdfBusy(false); }
    };

    const openShare = async () => {
        try {
            const { data } = await client.post(`/books/${book.id}/share`);
            const url = `${window.location.origin}/shared/${data.share_token}`;
            setShareLink(url);
            setBook((b) => ({ ...b, share_enabled: true, share_token: data.share_token }));
            setShareOpen(true);
        } catch { toast.error("Couldn't create the share link"); }
    };

    // The dialog has always promised this ("revoke anytime") — until now nothing
    // called it, so a link to a child's book could never be turned off.
    const revokeShare = async () => {
        setShareBusy(true);
        try {
            await client.delete(`/books/${book.id}/share`);
            setBook({ ...book, share_enabled: false });
            setShareLink("");
            setShareOpen(false);
            toast.success("Sharing turned off. That link no longer opens the book.");
        } catch {
            toast.error("Couldn't turn sharing off — please try again.");
        } finally { setShareBusy(false); }
    };

    const copyShare = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Link copied! Send it to Grandma 💌");
        } catch {}
    };

    const startPurchase = (kind) => {
        setBuyOpen(false);
        if (kind === "hardcover") { setAddrOpen(true); return; }
        payFor(kind, null);
    };

    const payFor = async (kind, shipping) => {
        try {
            const order = await payForBook({
                bookId: book.id, kind, method, shipping, title: book.title,
                contact: { name: user?.name, email: user?.email, phone: user?.phone },
            });
            // A UPI claim unlocks nothing yet — an admin confirms the credit first.
            if (order.upi) { setAddrOpen(false); setUpiOrder(order); return; }
            toast.success(kind === "pdf" ? "PDF unlocked!" : "Order placed — we'll ship it soon.");
            setBook({ ...book, purchased: true, purchase_kind: kind });
            setAddrOpen(false);
        } catch (e) {
            if (e?.cancelled) return;
            toast.error(e?.response?.data?.detail || e?.message || "Payment failed");
        }
    };

    return (
        <div className="min-h-screen reader-night">
            <Toaster position="top-center" richColors />
            <Navbar />
            <TerrySleeping />
            <div className="max-w-5xl mx-auto px-6 py-8">
                <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
                    <Link to="/library"><Button variant="ghost" size="sm" className="rounded-full text-paper hover:text-plum"><ArrowLeft className="w-4 h-4 mr-1" /> Library</Button></Link>
                    <div className="font-display text-2xl md:text-3xl tracking-tight text-paper text-center flex-1" data-testid="book-title">{book.title}</div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={toggleNarrator} className="rounded-full" data-testid="narrator-btn">
                            {narrating ? <VolumeX className="w-4 h-4 mr-1" /> : <Volume2 className="w-4 h-4 mr-1" />}
                            {narrating ? "Stop" : "Read aloud"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={openShare} className="rounded-full" data-testid="share-btn">
                            <Share2 className="w-4 h-4 mr-1" /> Share
                        </Button>
                        {!purchased ? (
                            <button className="btn-sticker text-sm" onClick={() => setBuyOpen(true)} data-testid="open-buy-btn">
                                ✨ Keep this book
                            </button>
                        ) : (
                            <button onClick={buildPDF} disabled={pdfBusy} className="btn-sticker sky text-sm disabled:opacity-60" data-testid="download-pdf-btn">
                                <Download className="w-4 h-4 mr-1 inline" /> {pdfBusy ? "Building…" : "Print Kit PDF"}
                            </button>
                        )}
                    </div>
                </div>

                {/* Family voice — one recording per page, in a voice that can't be regenerated */}
                <div className="mx-auto max-w-2xl mb-4 card-cloud px-4 py-2.5" data-testid="voice-bar">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-xs tracking-[0.2em] uppercase text-plum/60">
                            Read it in your own voice
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            {[leftPage, rightPage].filter(Boolean).map((p) => {
                                const n = p.page_number;
                                const has = voices[String(n)];
                                const isRec = recording === n;
                                const isUp = uploading === n;
                                // Same three free pages the preview shows — recording onto a
                                // page you cannot read yet is a button that leads nowhere.
                                const locked = !purchased && n > lockedFrom;
                                if (locked) return (
                                    <div key={n} className="rounded-full px-3 py-1.5 text-xs border-2 border-plum/10 text-plum/30 flex items-center gap-1.5">
                                        <Lock className="w-3 h-3" /> Page {n}
                                    </div>
                                );
                                return (
                                    <div key={n} className="flex items-center gap-1">
                                        <button
                                            onClick={() => (isRec ? stopRecording() : startRecording(n))}
                                            disabled={isUp || (recording !== null && !isRec)}
                                            data-testid={`voice-record-${n}`}
                                            aria-label={isRec ? `Stop recording page ${n}` : `Record page ${n}`}
                                            title={has ? `Recorded by ${has.by} — tap to replace` : `Record page ${n}`}
                                            className={`rounded-full px-3 py-1.5 text-xs font-medium border-2 flex items-center gap-1.5 transition-colors disabled:opacity-40 ${
                                                isRec ? "border-bubblegum bg-bubblegum text-white"
                                                      : has ? "border-bubblegum/40 text-plum bg-white"
                                                            : "border-plum/15 text-plum/70 hover:bg-white"
                                            }`}>
                                            {isUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                  : isRec ? <Square className="w-3 h-3 fill-current" />
                                                          : <Mic className="w-3.5 h-3.5" />}
                                            {isRec ? "Stop" : `Page ${n}`}
                                            {has && !isRec && !isUp && (
                                                <span className="text-[10px] text-plum/50">· {has.by}</span>
                                            )}
                                        </button>
                                        {has && !isRec && (
                                            <button onClick={() => clearVoice(n)} data-testid={`voice-clear-${n}`}
                                                aria-label={`Remove the recording on page ${n}`}
                                                className="text-plum/25 hover:text-bubblegum">
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Sticker palette */}
                <div className="mx-auto max-w-2xl mb-4 flex items-center justify-between card-cloud px-4 py-2.5" data-testid="sticker-palette">
                    <div className="text-xs tracking-[0.2em] uppercase text-plum/60">Tap a sticker, then tap the page</div>
                    <div className="flex items-center gap-2">
                        {STICKERS.map((s) => (
                            <button key={s.type}
                                onClick={() => setSelectedSticker(selectedSticker === s.type ? null : s.type)}
                                className={`w-9 h-9 rounded-full grid place-items-center border-2 transition-all ${selectedSticker === s.type ? "border-plum scale-110 bg-white" : "border-transparent hover:bg-white"}`}
                                style={{ boxShadow: selectedSticker === s.type ? `0 0 0 3px ${s.color}55` : "none" }}
                                data-testid={`sticker-${s.type}`}
                                title={s.type}>
                                <s.icon className="w-4 h-4" style={{ color: s.color, fill: s.fill ? s.color : "none" }} />
                            </button>
                        ))}
                        {selectedSticker && (
                            <button onClick={() => setSelectedSticker(null)} className="text-xs text-plum/60 ml-2" data-testid="sticker-clear">
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                {/* The open book — two leaves and a spine, the way the design
                    board draws it. The turning leaf is hinged on the spine. */}
                <div className="relative mx-auto max-w-4xl booklet-noselect booklet-blur-on-hide"
                     style={{ perspective: 2200 }}
                     onContextMenu={(e) => e.preventDefault()}>
                    <div className="relative flex aspect-[3/2] rounded-2xl overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.4)]">
                        <ReaderPage
                            side="left"
                            page={leftPage}
                            pageIndex={spreadStart}
                            total={pages.length}
                            boxRef={leftBoxRef}
                            stickers={reactionsFor(leftPage)}
                            stickerConfig={stickerConfig}
                            watermarked={!purchased}
                            locked={!purchased && spreadStart >= lockedFrom}
                            textInImage={book.text_in_image && lang === "en"}
                            placing={!!selectedSticker}
                            onPlace={placeSticker(leftPage, leftBoxRef)}
                            onRemoveSticker={leftPage && removeSticker(leftPage.page_number)}
                            onUnlock={() => setBuyOpen(true)}
                            style={{
                                transformOrigin: "right center",
                                transform: turning === "back" ? "rotateY(168deg)" : "rotateY(0deg)",
                                transition: `transform ${TURN_MS}ms cubic-bezier(.3,.8,.25,1)`,
                                backfaceVisibility: "hidden",
                            }}
                        />
                        <ReaderPage
                            side="right"
                            page={rightPage}
                            pageIndex={spreadStart + 1}
                            total={pages.length}
                            boxRef={rightBoxRef}
                            stickers={reactionsFor(rightPage)}
                            stickerConfig={stickerConfig}
                            watermarked={!purchased}
                            locked={!purchased && spreadStart + 1 >= lockedFrom}
                            textInImage={book.text_in_image && lang === "en"}
                            placing={!!selectedSticker}
                            onPlace={placeSticker(rightPage, rightBoxRef)}
                            onRemoveSticker={rightPage && removeSticker(rightPage.page_number)}
                            onUnlock={() => setBuyOpen(true)}
                            style={{
                                transformOrigin: "left center",
                                transform: turning === "fwd" ? "rotateY(-168deg)" : "rotateY(0deg)",
                                transition: `transform ${TURN_MS}ms cubic-bezier(.3,.8,.25,1)`,
                                backfaceVisibility: "hidden",
                            }}
                        />
                        {/* The binding itself */}
                        <div className="pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-plum/25" />
                    </div>

                    <div className="flex items-center justify-between mt-6">
                        <Button variant="outline" onClick={prev} disabled={spreadStart === 0 || !!turning}
                                className="rounded-full" data-testid="reader-prev-btn">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                        </Button>
                        <div className="text-xs text-paper/60" data-testid="reader-page-count">
                            {rightPage
                                ? `Pages ${leftPage?.page_number}–${rightPage.page_number} of ${pages.length}`
                                : `Page ${leftPage?.page_number} of ${pages.length}`}
                        </div>
                        <Button variant="outline" onClick={next} disabled={spreadStart + 2 >= pages.length || !!turning}
                                className="rounded-full" data-testid="reader-next-btn">
                            Next <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Purchase modal */}
            <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
                <DialogContent className="max-w-lg" data-testid="buy-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-plum">Keep your story forever.</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <button onClick={() => startPurchase("pdf")} className="card-cloud p-5 text-left hover:scale-[1.02] transition-transform" data-testid="buy-pdf-btn">
                            <div className="text-xs tracking-[0.2em] uppercase text-plum/60">Instant</div>
                            <div className="font-display text-xl mt-1">PDF Download</div>
                            <div className="font-display text-2xl text-bubblegum mt-3">₹399</div>
                            <div className="text-xs text-plum/50 mt-2">Print-friendly with cutting guides</div>
                        </button>
                        <button onClick={() => startPurchase("hardcover")} className="card-cloud p-5 text-left hover:scale-[1.02] transition-transform" data-testid="buy-hardcover-btn">
                            <div className="text-xs tracking-[0.2em] uppercase text-plum/60">Ships in 7 days</div>
                            <div className="font-display text-xl mt-1">Hardcover Book</div>
                            <div className="font-display text-2xl text-bubblegum mt-3">₹799</div>
                            <div className="text-xs text-plum/50 mt-2">Bound and delivered to your door</div>
                        </button>
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                        <span className="text-xs text-plum/50">Pay with</span>
                        <div className="flex gap-1 p-1 rounded-full bg-lavender/40" data-testid="reader-method-toggle">
                            {[["razorpay", "Card / netbanking"], ["upi", "UPI"]].map(([m, label]) => (
                                <button key={m} onClick={() => setMethod(m)} data-testid={`reader-method-${m}`}
                                    className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                                        method === m ? "bg-white text-plum shadow-sm" : "text-plum/60"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="text-xs text-plum/50 mt-3">
                        {method === "upi"
                            ? "Pay from any UPI app. We confirm it by hand, usually within a few hours."
                            : "Secure checkout via Razorpay."} Order updates sent to your registered contact.
                    </div>
                </DialogContent>
            </Dialog>

            {/* Share dialog */}
            <Dialog open={shareOpen} onOpenChange={setShareOpen}>
                <DialogContent className="max-w-lg" data-testid="share-dialog">
                    <DialogHeader><DialogTitle className="font-display text-2xl text-plum">Let Grandma leave stickers 💌</DialogTitle></DialogHeader>
                    <p className="text-sm text-plum/60 mt-2">
                        Anyone with this link can read the book and drop hearts, stars and rainbows on their favourite pages.
                        They don't need an account.
                    </p>
                    <div className="mt-4 flex items-center gap-2">
                        <input readOnly value={shareLink} onFocus={(e) => e.target.select()}
                            className="flex-1 border border-input rounded-full h-11 px-4 bg-cream text-sm font-mono text-plum"
                            data-testid="share-link-input" />
                        <button onClick={copyShare} className="btn-sticker sky text-sm" data-testid="share-copy-btn">
                            {copied ? <CheckIcon className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="text-xs text-plum/50 mt-3">
                        The book stays protected — no downloads for guests.
                    </div>
                    <div className="mt-4 pt-4 border-t border-plum/10 flex items-center justify-between gap-3">
                        <div className="text-xs text-plum/60">
                            Changed your mind? Turning this off breaks the link for everyone
                            who has it. Stickers already left are kept.
                        </div>
                        <Button variant="outline" size="sm" onClick={revokeShare} disabled={shareBusy}
                            className="rounded-full shrink-0 text-bubblegum border-bubblegum/40 hover:bg-bubblegum/5"
                            data-testid="share-revoke-btn">
                            {shareBusy ? "Turning off…" : "Stop sharing"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
                <DialogContent className="max-w-lg" data-testid="cart-dialog">
                    <DialogHeader><DialogTitle className="font-display text-2xl text-plum">Your cart</DialogTitle></DialogHeader>
                    <div className="card-cloud p-4 flex items-center gap-3">
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-lavender/50">
                            {pages[0]?.image_url && <img src={mediaUrl(pages[0].image_url)} className="w-full h-full object-cover" alt="" />}
                        </div>
                        <div className="flex-1">
                            <div className="font-medium text-plum">{book.title}</div>
                            <div className="text-xs text-plum/60">Hardcover, {pages.length} pages</div>
                        </div>
                        <div className="font-display text-lg text-bubblegum">₹799</div>
                    </div>
                    <div className="mt-4 grid gap-3">
                        <input className="border border-input rounded-full h-11 px-4 bg-white text-sm" placeholder="Full name"
                            value={addr.name} maxLength={60} onChange={(e) => setAddr({ ...addr, name: e.target.value })} data-testid="ship-name" />
                        <input className="border border-input rounded-full h-11 px-4 bg-white text-sm" placeholder="Address line"
                            value={addr.line1} maxLength={200} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="ship-line1" />
                        <div className="grid grid-cols-2 gap-3">
                            <input className="border border-input rounded-full h-11 px-4 bg-white text-sm" placeholder="City"
                                value={addr.city} maxLength={120} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="ship-city" />
                            <input className="border border-input rounded-full h-11 px-4 bg-white text-sm" placeholder="PIN code"
                                value={addr.pin} maxLength={6} inputMode="numeric" data-testid="ship-pin"
                                onChange={(e) => setAddr({ ...addr, pin: e.target.value.replace(/\D/g, "") })} />
                        </div>
                        <input className="border border-input rounded-full h-11 px-4 bg-white text-sm" placeholder="Contact phone"
                            value={addr.phone} maxLength={20} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="ship-phone" />
                    </div>
                    {addrError && Object.values(addr).some(Boolean) && (
                        <div className="text-xs text-bubblegum mt-3" data-testid="cart-addr-error">{addrError}</div>
                    )}
                    <button className="mt-4 btn-sticker w-full disabled:opacity-60"
                        onClick={() => payFor("hardcover", addr)}
                        disabled={!!addrError}
                        data-testid="proceed-payment-btn">
                        Proceed to Payment · ₹799
                    </button>
                </DialogContent>
            </Dialog>

            <UpiPay order={upiOrder} open={!!upiOrder}
                onOpenChange={(o) => { if (!o) setUpiOrder(null); }}
                onClaimed={() => {
                    setUpiOrder(null);
                    toast.success("Thanks — we'll confirm your payment and email you.");
                }} />
        </div>
    );
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            // Draw to canvas to normalise into PNG data (handles SVG data URLs and CORS)
            try {
                const canvas = document.createElement("canvas");
                canvas.width = img.naturalWidth || 800;
                canvas.height = img.naturalHeight || 1000;
                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#FFFBF5";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/png"));
            } catch (e) {
                resolve(src);
            }
        };
        img.onerror = reject;
        img.src = src;
    });
}
